import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  BulkCreateUsersFromFileDocument,
  type BulkCreateUsersFromFileMutation,
  type BulkCreateUsersFromFileMutationVariables,
  GetUsersDocument,
} from "~/features/admin/users/gqls";
import { translateError } from "~/lib/errors";

export type BulkCreateUsersResult = NonNullable<BulkCreateUsersFromFileMutation["cnutBulkCreateUsersFromFile"]>;

interface UseBulkUserCreateOptions {
  onSuccess?: (result: BulkCreateUsersResult) => void;
}

export function useBulkUserCreate({ onSuccess }: UseBulkUserCreateOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [bulkCreateUsers] = useMutation<BulkCreateUsersFromFileMutation, BulkCreateUsersFromFileMutationVariables>(
    BulkCreateUsersFromFileDocument,
    { refetchQueries: [GetUsersDocument] },
  );

  const submit = useCallback(
    async (file: File) => {
      setProcessing(true);
      try {
        const result = await bulkCreateUsers({ variables: { input: { file: [file] } } });

        const data = result.data?.cnutBulkCreateUsersFromFile;
        if (data) {
          const { successCount, failureCount } = data;
          const savedCount = data.failedUsers.filter((failure) => failure.savedUserId).length;

          if (savedCount > 0) {
            toast.warning(t("model.users.bulkCreate.savedRetrySummary", { count: savedCount }));
          } else if (failureCount === 0) {
            toast.success(t("feature.common.createdSuccessfully", { model: "user" }));
          } else if (successCount === 0) {
            toast.error(t("feature.common.failedToCreate", { model: "user" }));
          } else {
            toast.info(t("model.users.bulkCreate.partialSuccess", { success: successCount, failed: failureCount }));
          }

          onSuccess?.(data);
        }
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [bulkCreateUsers, t, onSuccess],
  );

  return { submit, processing };
}
